import { CheckIcon, Loader2Icon, PlusIcon, SearchIcon } from "lucide-react"
import type * as React from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useCustomerSearch, useItemSearch } from "@/features/invoices/use-invoice-editor"
import { useSaveCustomer } from "@/features/customers/use-customer"
import { GST_STATE_CODE_LIST } from "@steelbill/types"
import { toast } from "sonner"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { ApiError } from "@/lib/api-error"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import type { Customer, Item } from "@/lib/endpoints"
import { formatRupees } from "@/lib/money"
import { basisPointsToInputValue } from "@/lib/units"

/**
 * The two pickers the invoice editor needs (§11.1).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A FULL-SCREEN DIALOG, NOT A DROPDOWN, and that is a §11.1 decision rather than
 * a stylistic one. The owner is choosing from a searchable list on a phone, in a
 * workshop, with a thumb. A combobox popover gives him a 200px-tall scroll area
 * with 32px rows; a dialog gives him the whole screen, a real keyboard-summoning
 * search field, and rows big enough to hit without looking.
 *
 * SEARCH IS SERVER-SIDE IN BOTH. Customers match on name, phone or GSTIN; items
 * match on name, description or HSN code. Filtering a fetched page in the
 * browser would behave identically on a short list and silently miss everything
 * past the first twenty — which is exactly when the owner needs it to work.
 *
 * THE ITEM LIST OPENS ON THE MOST-USED JOBS. With no search text the server
 * orders by `usage_count` (§11.1 — "the jobs he bills every week are one tap
 * away"). A fabricator bills the same dozen jobs over and over.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/**
 * `27 — Maharashtra`, built once at module load.
 *
 * ONE SOURCE FOR BOTH SIDES OF THE CONTROL: the row in the open list and the text
 * on the closed trigger. Base UI's `Select.Value` prints the RAW value unless it
 * is told how to turn one into words, and the raw value here is `27` — a state
 * CODE on the field that decides CGST+SGST versus IGST, which is the one place a
 * half-answer is a tax error rather than an untidy screen.
 *
 * Static because `GST_STATE_CODE_LIST` is frozen and carries no translated text;
 * a stable identity also keeps Base UI from re-syncing its store every render.
 */
const STATE_LABELS = new Map<string, string>(
  GST_STATE_CODE_LIST.map((state) => [state.code, `${state.code} — ${state.name}`]),
)
const stateLabel = (code: string): string => STATE_LABELS.get(code) ?? code

/*
 * ═════════════════════════════════════════════════════════════════════════════
 * 44px ROWS, AND THIS IS NOT DECORATION.
 *
 * The state picker below was a native `<select>` on purpose: on a phone the
 * operating system opens its own full-height wheel — bigger than anything drawn
 * in the page, already familiar, and reliably hittable without a 48px target
 * (§11.1). The owner asked for the in-page Select instead, for visual consistency
 * with the rest of the app, and that is his call. But the property the native
 * control was protecting is the one thing the swap must not lose, and shadcn's
 * `SelectItem` is about 28px (`py-1` around `text-sm`) — 16px short of a thumb
 * with dust on it. So the height is put back here, explicitly.
 *
 * ``, NEVER `min-h-[44px]`: `styles/preferences.css` scales the root font
 * size for the large-text preference precisely so touch targets grow with it, and
 * a pixel literal would stay stuck at 44. Unconditional rather than behind
 * `pointer-coarse:` — the workshop phone is the primary device, a desktop mouse
 * loses nothing from a 44px row, and an unconditional class cannot be defeated by
 * a device that misreports its pointer type.
 *
 * THE LABELS MUST BE ABLE TO WRAP, and the popup must be able to outgrow the
 * trigger. `SelectContent` is `w-(--anchor-width)` with `overflow-x-hidden` and
 * the item's text is `whitespace-nowrap`, so "26 — Dadra and Nagar Haveli and
 * Daman and Diu" would be half-read inside this dialog's half-width column. A
 * half-read state name here is a wrong tax, not a cosmetic complaint.
 *
 * Do not "tidy" any of this away.
 * ═════════════════════════════════════════════════════════════════════════════
 */
const TOUCH_ITEM = "whitespace-normal [&>div]:whitespace-normal"
/*
 * THE TRIGGER IS DELIBERATELY NOT FORCED TALLER, and it was until
 * 2026-08-16. At 44px it stood a head above the search box and the "More
 * filters" button beside it — both `h-8` — and read as a rendering fault. The
 * OS wheel this replaced was a LIST, so the 44px minimum belongs on the ITEMS
 * (see `TOUCH_ITEM`, which the shared component now applies anyway); the
 * trigger is one control among several the same size. If a toolbar's targets
 * are ever raised, raise every control in it together.
 */

export function CustomerPicker({
  open,
  onOpenChange,
  onPick,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onPick: (customer: Customer) => void
}): React.JSX.Element {
  const { t } = useTranslation()
  const [q, setQ] = useState("")
  const [adding, setAdding] = useState(false)
  const query = useCustomerSearch(q)
  const rows = query.data?.data ?? []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col gap-3 sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("customer.pickTitle")}</DialogTitle>
          <DialogDescription>{t("customer.pickHelp")}</DialogDescription>
        </DialogHeader>

        <SearchField
          value={q}
          onChange={setQ}
          label={t("customer.searchLabel")}
          placeholder={t("customer.searchPlaceholder")}
        />

        {/*
          * "+ New customer" WITHOUT LEAVING THE BILL. The editor's state lives
          * in memory, so navigating to the Customers screen to add a buyer
          * threw away every line already typed — the most expensive dead end in
          * the app. The quick form takes the minimum a bill legally needs (name
          * and state, GSTIN when they have one); everything else can be filled
          * in on the customer screen later.
          */}
        {adding ? (
          <QuickCustomerForm
            initialName={q}
            onCreated={(created) => {
              onPick(created)
              onOpenChange(false)
              setAdding(false)
            }}
            onCancel={() => {
              setAdding(false)
            }}
          />
        ) : (
          <Button
            type="button"
            variant="outline"
            className="self-start"
            onClick={() => {
              setAdding(true)
            }}
          >
            <PlusIcon />
            {t("btn.addNewCustomer")}
          </Button>
        )}

        <div className="-mx-6 flex-1 overflow-y-auto border-t">
          {query.isPending ? (
            <RowSkeletons />
          ) : rows.length === 0 ? (
            <p className="text-muted-foreground p-6 text-center text-sm">
              {t("customer.noneFound")}
            </p>
          ) : (
            <ul className="flex flex-col">
              {rows.map((customer) => (
                <li key={customer.id} className="border-b last:border-b-0">
                  <button
                    type="button"
                    onClick={() => {
                      onPick(customer)
                      onOpenChange(false)
                    }}
                    className="hover:bg-accent/50 flex min-h-14 w-full flex-col items-start gap-0.5 px-6 py-3 text-left transition-colors"
                  >
                    <span className="font-medium">{customer.name}</span>
                    <span className="text-muted-foreground text-xs tabular-nums">
                      {customer.gstin ?? t("customer.noGstin")} · {customer.stateName} (
                      {customer.stateCode})
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export function ItemPicker({
  open,
  onOpenChange,
  onPick,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onPick: (item: Item) => void
}): React.JSX.Element {
  const { t } = useTranslation()
  const [q, setQ] = useState("")
  const query = useItemSearch(q)
  const rows = query.data?.items ?? []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col gap-3 sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("line.pickerTitle")}</DialogTitle>
          <DialogDescription>{t("line.suggestions")}</DialogDescription>
        </DialogHeader>

        <SearchField
          value={q}
          onChange={setQ}
          label={t("line.pickerSearch")}
          placeholder={t("line.pickerSearch")}
        />

        <div className="-mx-6 flex-1 overflow-y-auto border-t">
          {query.isPending ? (
            <RowSkeletons />
          ) : rows.length === 0 ? (
            <p className="text-muted-foreground p-6 text-center text-sm">{t("line.pickerEmpty")}</p>
          ) : (
            <ul className="flex flex-col">
              {rows.map((item) => (
                <li key={item.id} className="border-b last:border-b-0">
                  <button
                    type="button"
                    onClick={() => {
                      onPick(item)
                      onOpenChange(false)
                    }}
                    className="hover:bg-accent/50 flex min-h-14 w-full items-center gap-3 px-6 py-3 text-left transition-colors"
                  >
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="font-medium">{item.name}</span>
                      <span className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs tabular-nums">
                        <span>HSN {item.hsn}</span>
                        <Badge variant="outline" className="text-[0.65rem]">
                          {basisPointsToInputValue(item.gstRateBp)}%
                        </Badge>
                      </span>
                    </span>
                    {/* The saved rate, so the owner recognises the job by its
                      * price as much as by its name. */}
                    <span className="shrink-0 font-medium tabular-nums">
                      {formatRupees(item.ratePaise)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** The chosen customer, shown on the editor with a way to change it. */
export function ChosenCustomer({
  customer,
  onChange,
}: {
  customer: Customer | null
  onChange: () => void
}): React.JSX.Element {
  const { t } = useTranslation()

  if (customer === null) {
    return (
      <Button type="button" variant="outline" onClick={onChange} className="w-full justify-start">
        <SearchIcon />
        {t("customer.choosePrompt")}
      </Button>
    )
  }

  return (
    <div className="flex items-start justify-between gap-3 rounded-md border p-3">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="flex flex-wrap items-center gap-2">
          <CheckIcon aria-hidden="true" className="size-4 shrink-0" />
          <span className="font-medium">{customer.name}</span>
        </span>
        <span className="text-muted-foreground text-xs tabular-nums">
          {customer.gstin ?? t("customer.noGstin")} · {customer.stateName} ({customer.stateCode})
        </span>
        {/*
          * WHICH TAXES THIS BILL WILL CARRY, said before the lines are entered.
          * It follows from the customer's state (§6) and is the single most
          * consequential consequence of this choice — but the SERVER decides it,
          * and the preview below is what states it authoritatively. This is a
          * heads-up, not the source of truth.
          */}
        <span className="text-muted-foreground text-xs">
          {customer.stateCode === "27"
            ? t("customer.taxWillBeCgstSgst")
            : t("customer.taxWillBeIgst")}
        </span>
      </div>
      <Button type="button" variant="ghost" onClick={onChange}>
        {t("btn.edit")}
      </Button>
    </div>
  )
}

function SearchField({
  value,
  onChange,
  label,
  placeholder,
}: {
  value: string
  onChange: (value: string) => void
  label: string
  placeholder: string
}): React.JSX.Element {
  return (
    <div className="relative">
      <SearchIcon
        aria-hidden="true"
        className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
      />
      <Input
        type="search"
        className="pl-9"
        aria-label={label}
        placeholder={placeholder}
        value={value}
        onChange={(event) => {
          onChange(event.target.value)
        }}
        autoFocus
      />
    </div>
  )
}

function RowSkeletons(): React.JSX.Element {
  return (
    <ul className="flex flex-col">
      {[0, 1, 2, 3].map((index) => (
        <li key={index} className="flex flex-col gap-2 border-b px-6 py-4 last:border-b-0">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3 w-56" />
        </li>
      ))}
    </ul>
  )
}

/**
 * The minimum a bill legally needs of a buyer: a name, and a state (§5 — the
 * state decides CGST+SGST vs IGST) or a GSTIN whose first two digits carry it.
 * Everything else — address, contact, vendor code — is the customer screen's
 * business, later. POSTs the same `CustomerCreateSchema` route the full editor
 * uses, so the validation cannot drift.
 */
function QuickCustomerForm({
  initialName,
  onCreated,
  onCancel,
}: {
  initialName: string
  onCreated: (customer: Customer) => void
  onCancel: () => void
}): React.JSX.Element {
  const { t } = useTranslation()
  const save = useSaveCustomer(undefined)
  const [name, setName] = useState(initialName)
  const [gstin, setGstin] = useState("")
  // Maharashtra — this business's own state, and nearly every buyer's.
  const [stateCode, setStateCode] = useState("27")

  const submit = () => {
    const trimmedGstin = gstin.trim().toUpperCase()
    save.mutate(
      {
        name: name.trim(),
        // GSTIN wins over the state pick — the server derives the state from
        // its first two digits, exactly as the full editor does.
        ...(trimmedGstin === "" ? { stateCode } : { gstin: trimmedGstin }),
      },
      {
        onSuccess: (created) => {
          toast.success(t("customer.saved"))
          onCreated(created)
        },
        onError: (error) => {
          toast.error(error instanceof ApiError ? error.message : t("err.somethingWentWrong"))
        },
      },
    )
  }

  return (
    <div className="flex flex-col gap-3 rounded-md border p-3">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="quick-customer-name">{t("lbl.customerName")}</Label>
        <Input
          id="quick-customer-name"
          value={name}
          autoFocus
          onChange={(event) => {
            setName(event.target.value)
          }}
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="quick-customer-gstin">{t("lbl.gstin")}</Label>
          <Input
            id="quick-customer-gstin"
            autoCapitalize="characters"
            value={gstin}
            onChange={(event) => {
              setGstin(event.target.value)
            }}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="quick-customer-state">{t("lbl.state")}</Label>
          <Select
            value={stateCode}
            /*
             * A GSTIN DECIDES THE STATE, so once one is typed this box is locked
             * rather than left to disagree with it — the server derives the state
             * from the GSTIN's first two digits, and a customer whose GSTIN says
             * Gujarat billed as Maharashtra is CGST+SGST where IGST is due.
             * `disabled` on the Root reaches the trigger (`SelectTrigger` merges
             * `fieldDisabled || selectDisabled || disabledProp`), which is what
             * the `disabled:opacity-50` styling needs.
             */
            disabled={gstin.trim() !== ""}
            itemToStringLabel={stateLabel}
            onValueChange={(code) => {
              // Typed to allow null; there is no empty option here — the field
              // defaults to Maharashtra — so there is nothing to clear it to.
              if (code === null) return
              setStateCode(code)
            }}
          >
            <SelectTrigger id="quick-customer-state" className="w-full">
              <SelectValue />
            </SelectTrigger>
            {/* Wider than the trigger when it needs to be — see the note on
              * `TOUCH_ITEM`. `w-auto` lets the popup grow past this dialog's
              * half-width column; `min-w-(--anchor-width)` keeps it from being
              * narrower than the box it drops out of. */}
            <SelectContent className="w-auto max-w-[min(90vw,28rem)] min-w-(--anchor-width)">
              {GST_STATE_CODE_LIST.map((state) => (
                <SelectItem className={TOUCH_ITEM} key={state.code} value={state.code}>
                  {stateLabel(state.code)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t("btn.cancel")}
        </Button>
        <Button
          type="button"
          disabled={name.trim() === "" || save.isPending}
          onClick={submit}
        >
          {save.isPending ? <Loader2Icon className="animate-spin" /> : null}
          {t("btn.saveAndUse")}
        </Button>
      </div>
    </div>
  )
}
